import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Tag } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import qs from 'qs';
import { useGetContentReviews } from 'src/reactQuery/contentReview/useGetContentReviewsQuery';
import NoxTable from '../../component/Table';
import InnerHeader from '../../component/InnerHeader';
import { deleteContentReview } from '../../services/napi';
import { fetchContentReviews } from '../../actions/contentReviewAction';
import Copy from '../../component/Copy';
import formatDate from '../../helpers/formatDate';

const ContentReview = () => {
  const history = useHistory();
  const urlLocation = useLocation();
  const dispatch = useDispatch();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isDragAndDropEnabled, setIsDragAndDropEnabled] = useState(false);
  const params = qs.parse(urlLocation.search, { ignoreQueryPrefix: true });

  const { data: contentReviewData, isLoading } = useGetContentReviews({
    orderBy: params.orderBy,
    orderDirection: params.orderDirection,
    page: params.page,
    pageSize: params.pageSize
  });

  const mapToTable = (contentReview) => {
    const data = [];

    if (!contentReview || contentReview.length === 0) {
      return data;
    }

    contentReview.forEach(
      ({ _id, name, customTypeName, created, updated, status, reviewStatus, author }, index) => {
        const updateDate = formatDate(updated);
        const createdDate = formatDate(created);
        data.push({
          _id,
          by: author ? author.email : 'Me',
          created: createdDate,
          customTypeName,
          key: index,
          name,
          status: reviewStatus || status,
          updated: updateDate
        });
      }
    );

    return data;
  };

  const filterData = (data) => {
    const selectedFilter = getInitialFilterFromUrl();

    if (selectedFilter[0] === 'All' && selectedFilter[1] === 'All') {
      return data;
    }
    if (selectedFilter[0] === 'All' && selectedFilter[1] !== 'All') {
      return data.filter((item) => item.status === selectedFilter[1]);
    }
    if (selectedFilter[0] !== 'All' && selectedFilter[1] === 'All') {
      return data.filter((item) => item.contentType === selectedFilter[0]);
    }
    return data.filter(
      (item) => item.contentType === selectedFilter[0] && item.status === selectedFilter[1]
    );
  };

  const openDetail = (item) => {
    history.push(`/content-review/${item._id}`);
  };

  const columns = [
    {
      dataIndex: 'name',
      key: 'name',
      render: (name) => {
        return (
          <div
            style={{
              color: 'black',
              fontWeight: 700
              // width: "256px",
              // textAlign: "start",
            }}
          >
            <Copy text={name} />
          </div>
        );
      },
      sorter: true,
      title: 'Name'
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let style = {};

        if (status === 'pending') {
          style = {
            backgroundColor: 'rgba(155, 160, 173, 0.15)',
            color: '#9BA0AD'
          };
        }

        if (status === 'rejected') {
          style = {
            backgroundColor: 'rgba(255, 71, 108, 0.15)',
            color: '#FF476C'
          };
        }

        if (status === 'accepted') {
          style = {
            backgroundColor: 'rgba(65, 204, 156, 0.15)',
            color: '#41CC9C'
          };
        }
        return (
          <Tag
            key={status}
            style={{
              ...style,
              border: 'none',
              cursor: 'default',
              width: '90%'
            }}
          >
            {status}
          </Tag>
        );
      },
      sorter: true,
      title: 'Status'
    },
    {
      dataIndex: 'customTypeName',
      key: 'customTypeName',
      render: (customTypeName) => {
        return <Copy text={customTypeName} />;
      },
      sorter: true,
      title: 'Content Model'
    },
    {
      dataIndex: 'created',
      key: 'created',
      render: (created) => {
        return <Copy text={created} />;
      },
      sorter: true,
      title: 'Created'
    },
    {
      dataIndex: 'updated',
      key: 'updated',
      render: (update) => {
        return <Copy text={update} />;
      },
      sorter: true,
      title: 'Updated'
    },
    {
      dataIndex: 'by',
      key: 'by',
      render: (by) => {
        return <Copy text={by} />;
      },
      sorter: (a, b) => a.by.localeCompare(b.by),
      title: 'Author'
    },
    {
      key: 'edit',
      render: (item) => {
        return <Tag onClick={() => openDetail(item)}>edit</Tag>;
      }
    }
  ];

  const projectId = useSelector(({ project }) => project.currentProject._id);

  const onContentDelete = async () => {
    await Promise.all(
      selectedRowKeys.map(async (index) => {
        const item = data[index];

        await deleteContentReview(item._id, projectId);
      })
    );

    setSelectedRowKeys([]);
    dispatch(fetchContentReviews());
  };

  const getInitialFilterFromUrl = () => {
    const { filter } = qs.parse(urlLocation.search, {
      ignoreQueryPrefix: true
    });

    if (typeof filter === 'string') {
      return [filter, 'All'];
    }

    return filter || [filterListContentModel[0], filterListStatus[0]];
  };

  const actions = [];

  if (selectedRowKeys.length) {
    actions.push({
      onClick: onContentDelete,
      title: 'Delete',
      type: 'primary'
    });
  }

  useEffect(() => {
    if (params && params.filter && params.filter[0] && params.filter[0] !== 'All') {
      setIsDragAndDropEnabled(true);
    }
  }, [params]);

  const data = mapToTable(contentReviewData?.reviews);

  const filterListContentModel = ['All', ...new Set(data.map((item) => item.customTypeName))];
  const filterListStatus = ['All', ...new Set(data.map((item) => item.status))];

  const filteredData = filterData(data);

  const onFilterChange = (value, index, selectedFilter) => {
    const params = qs.parse(urlLocation.search, { ignoreQueryPrefix: true });

    if (index === 0) {
      params.filter = [value, selectedFilter];
    } else {
      params.filter = [selectedFilter, value];
    }

    if (!params.filter[0] || params.filter[0] === 'All') {
      setIsDragAndDropEnabled(false);
    } else {
      setIsDragAndDropEnabled(true);
    }

    history.push({
      pathname: `/content-review`,
      search: qs.stringify(params)
    });
  };

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={contentReviewData?.paging.total} title="Content Review" />
      <NoxTable
        actions={actions}
        columns={columns}
        data={filteredData}
        dragAndDropUrl="content"
        filterContentModel={filterListContentModel}
        filterList={[filterListContentModel, filterListStatus]}
        filterStatus={filterListStatus}
        isDragAndDropEnabled={isDragAndDropEnabled}
        loading={isLoading}
        onFilterChange={onFilterChange}
        selectedFilter={getInitialFilterFromUrl()}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        totalPages={contentReviewData?.paging.total}
      />
    </div>
  );
};

export default ContentReview;
